import { Actions, EventManager, Events } from "../../lib/Events";
import axios from "axios";
import CommonConditions from "@lib/commonConditions";
import { useCallState } from "@/stores/videoCallStateParts/callState";

const EVENT_QUEUE_KEY = "ecv-pending-call-events";

function loadQueue() {
  try {
    const raw = localStorage.getItem(EVENT_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  try {
    localStorage.setItem(EVENT_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore storage errors
  }
}

function isCurrentCall(rtcId) {
  return window.location.href.includes(rtcId);
}

function isOnCallPage() {
  return /\/r\/[^/]+/.test(window.location.pathname);
}

async function trySendEventItem(item) {
  if (!isOnCallPage()) {
    console.log("Call event " + item.name + " skipped, not on a call page (id: " + item.id + ")");
    return false;
  }

  const isCurrent = isCurrentCall(item.rtcId);
  const config = !isCurrent && item.agentToken ? { params: { agentToken: item.agentToken } } : {};

  try {
    const response = await axios.post(
      "/api/call/event",
      { rtcId: item.rtcId, data: item.data, CallEventTypeName: item.name, Timestamp: item.timestamp },
      config,
    );
    if (!response.data?.success) {
      console.error("Call event " + item.name + " returned non-success response, will retry later (id: " + item.id + ")");
      return false;
    }
    const queue = loadQueue();
    saveQueue(queue.filter((e) => e.id !== item.id));
    console.log("Call event " + item.name + " sent and removed from queue");
    return true;
  } catch (e) {
    if (e.response?.status >= 400 && e.response?.status < 500) {
      console.error(
        "Call event " + item.name + " got " + e.response.status + ", dropping from queue (id: " + item.id + ")",
        e.response.data,
      );
      const queue = loadQueue();
      saveQueue(queue.filter((q) => q.id !== item.id));
      return false;
    }
    console.error("Call event " + item.name + " failed, will retry later (id: " + item.id + ")");
    return false;
  }
}

let retryInterval = null;

async function flushQueue() {
  const queue = loadQueue();
  for (const item of queue) {
    await trySendEventItem(item);
  }
}

export default function startEventCollecting() {
  if (!retryInterval) {
    retryInterval = setInterval(flushQueue, 30000);
  }

  EventManager.rootEventEmitter().on(Events.VideoCallCreated, (payload) => {
    if (payload.ignoreWebRtcRoom) {
      console.log("Ignoring call event collector for rtcId: " + payload.rtcId);
      return;
    }
    const rtcId = payload.rtcId;

    if (!CommonConditions.isAgent(rtcId)) return;

    const scopedEventEmitter = EventManager.getEventEmitter(rtcId);
    const scopedActionEmitter = EventManager.getActionEmitter(rtcId);

    const state = useCallState(rtcId);

    async function sendEvent(name, data = "") {
      console.log("Sending call event " + name + " data:" + data);
      if (state.muteEvents.value) {
        console.log("Mute events is enabled, not sending event " + name);
        return;
      }

      const item = {
        id: Date.now() + "_" + Math.random().toString(36).slice(2, 9),
        rtcId,
        name,
        data,
        timestamp: new Date().toISOString(),
        agentToken: new URLSearchParams(window.location.search).get("agentToken") || "",
      };

      const queue = loadQueue();
      queue.push(item);
      saveQueue(queue);
      console.log("Call event " + name + " queued (id: " + item.id + ")");

      await trySendEventItem(item);
    }

    scopedEventEmitter.on(Events.CallEvent, async (payload) => {
      try {
        const name = payload.name;
        const data = payload.data;
        await sendEvent(name, data);
      } catch (e) {
        console.error("Error while sending call event to server");
        console.error(e);
      }
    });

    scopedEventEmitter.on(Events.ConnectionStateChanged, async (payload) => {
      if (payload == "RTC-CONNECTION-CONNECTED") {
        sendEvent("Connected");
      }

      if (payload == "RTC-CONNECTION-FAILED") {
        sendEvent("ConnectionFailed");
      }

      if (payload == "RTC-CONNECTION-DISCONNECTED") {
        sendEvent("Disconnected");
      }

      if (payload == "ICE-CONNECTION-CONNECTED") {
        sendEvent("IceConnectionConnected");
      }

      if (payload == "ICE-CONNECTION-FAILED") {
        sendEvent("IceConnectionFailed");
      }

      if (payload == "ICE-CONNECTION-DISCONNECTED") {
        sendEvent("IceConnectionDisconnected");
      }
    });

    scopedEventEmitter.on(Events.RecordingStarted, async () => {
      await sendEvent("RecordingStarted");
    });

    scopedActionEmitter.on(Actions.HoldCall, async () => {
      await sendEvent("HoldStarted");
    });

    scopedActionEmitter.on(Actions.ResumeCall, async () => {
      await sendEvent("HoldFinished");
    });

    scopedActionEmitter.on(Actions.HangUp, async (payload) => {
      await sendEvent("Hangup", payload.hangupBy);
    });

    scopedActionEmitter.on(Actions.MuteAudio, async (payload) => {
      if (payload && payload.reason === "hold") {
        console.log("MuteAudio due to hold, not sending event");
        return;
      }
      await sendEvent("AudioMute");
    });

    scopedActionEmitter.on(Actions.UnmuteAudio, async (payload) => {
      if (payload && payload.reason === "hold") {
        console.log("UnmuteAudio due to hold, not sending event");
        return;
      }
      await sendEvent("AudioUnmute");
    });

    scopedActionEmitter.on(Actions.MuteVideo, async (payload) => {
      if (payload && payload.reason === "hold") {
        console.log("MuteVideo due to hold, not sending event");
        return;
      }
      await sendEvent("VideoMute");
    });

    scopedActionEmitter.on(Actions.UnmuteVideo, async (payload) => {
      if (payload && payload.reason === "hold") {
        console.log("UnmuteVideo due to hold, not sending event");
        return;
      }
      await sendEvent("VideoUnmute");
    });

    scopedEventEmitter.on(Events.ScreenShareStarted, async () => {
      await sendEvent("ScreenShareStarted");
    });

    scopedEventEmitter.on(Events.ScreenShareFinished, async () => {
      await sendEvent("ScreenShareFinished");
    });
  });
}
